import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { feedbackApi } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import ImpersonationBanner from "./ImpersonationBanner";
import WhatsNewDialog from "./WhatsNewDialog";
import InactivityWarningDialog from "./InactivityWarningDialog";
import { useInactivityTimeout } from "../hooks/useInactivityTimeout";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Home as HomeIcon,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ArrowLeft,
  PanelLeftClose,
  PanelLeftOpen,
  FileText,
  MessageSquare,
  Key,
  KeyRound,
  DollarSign,
  TrendingUp,
  FileSignature,
  Wallet,
  Calculator,
  GraduationCap,
  BookOpen,
  Banknote,
  Landmark,
  Contact,
  Moon,
  Sun,
  LayoutList,
  ClipboardList,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const Layout = () => {
  const { user, logout, isAdmin, isImpersonating, tracksStats, tracksBillings, isSupervisor } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadFeedback, setUnreadFeedback] = useState(0);
  // Desktop collapsed state (icon-only rail). Default collapsed on first visit,
  // user preference persisted in localStorage.
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("sidebar_collapsed");
    return v === null ? true : v === "1";
  });

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("sidebar_collapsed", next ? "1" : "0");
      }
      return next;
    });
  };

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const res = await feedbackApi.getUnreadCount();
      setUnreadFeedback(res.data.unread_count);
    } catch (error) {
      // Silently fail
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // ---- Inactivity auto-logout (4 hours, 60-second warning) ----
  const handleInactivityLogout = useCallback(() => {
    toast.warning("Signed out due to inactivity.");
    logout();
    navigate("/login");
  }, [logout, navigate]);

  const { showWarning, secondsLeft, stayLoggedIn } = useInactivityTimeout({
    enabled: !!user,
    timeoutMs: 4 * 60 * 60 * 1000, // 4 hours
    warningMs: 60 * 1000, // 60-second warning
    onLogout: handleInactivityLogout,
  });
  
  // Check if admin also tracks personal stats (like partners)
  const adminHasPersonalStats = isAdmin && (tracksStats || tracksBillings);

  // ---------- Sidebar groups (role-aware) ----------
  // Each item carries the roles allowed to see it. Groups whose items all
  // filter out for the current role are auto-hidden from rendering.
  const ROLE_ALL = ["admin", "supervisor", "employee"];
  const onShelf = user?.shelf_participant !== false;
  const groups = [
    {
      label: "Overview",
      items: [
        { to: "/", icon: HomeIcon, label: "Home", end: true, roles: ROLE_ALL },
        { to: "/admin", icon: LayoutDashboard, label: "Firm Overview", roles: ["admin"] },
        { to: "/supervisor", icon: Users, label: "Employee Stats", roles: ["supervisor"] },
        { to: "/employees", icon: Users, label: "Employee Stats", roles: ["admin"] },
        // My Stats: everyone except admins who don't track their own numbers
        ...(isAdmin && !adminHasPersonalStats
          ? []
          : [{ to: "/dashboard", icon: TrendingUp, label: "My Stats", roles: ROLE_ALL }]),
      ],
    },
    {
      label: "Firm Operations",
      items: [
        { to: "/stats", icon: BarChart3, label: "Stats Entry", roles: ["admin"] },
        { to: "/billings", icon: DollarSign, label: "Partner Billings", roles: ["admin"] },
        { to: "/financials", icon: Wallet, label: "A/R, WIP & Cash", roles: ["admin"] },
        { to: "/benchmarks", icon: Settings, label: "Benchmarks", roles: ["admin"] },
      ],
    },
    {
      label: "Client Work",
      items: [
        ...(onShelf
          ? [
              { to: "/shelf", icon: LayoutList, label: "Digital Shelf", roles: ROLE_ALL },
              { to: "/shelf/board", icon: ClipboardList, label: "Shelf Board", roles: ["admin"] },
            ]
          : []),
        { to: "/tax-calculator", icon: Calculator, label: "Tax Calculator", roles: ROLE_ALL },
        { to: "/amortization", icon: Landmark, label: "Amortization", roles: ROLE_ALL },
        { to: "/templates", icon: FileSignature, label: "Firm Templates", roles: ROLE_ALL },
        { to: "/w9", icon: FileSignature, label: "Request W-9", roles: ROLE_ALL },
        {
          to: "https://clientfinancials.steelemartin.com/sso?token=__TOKEN__",
          icon: Banknote,
          label: "Client Financial Platform",
          crossDomain: true,
          roles: ROLE_ALL,
        },
      ],
    },
    {
      label: "Resources",
      items: [
        { to: "/contacts", icon: Contact, label: "Contact Directory", roles: ROLE_ALL },
        { to: "/handbook", icon: BookOpen, label: "Employee Handbook", roles: ROLE_ALL },
        { to: "/training", icon: GraduationCap, label: "Training", roles: ROLE_ALL },
        { to: "/documents", icon: FileText, label: "Documents", roles: ROLE_ALL },
      ],
    },
    {
      label: "Tools",
      items: [
        { to: "/credentials", icon: Key, label: "Credentials", roles: ["admin"] },
        { to: "/passwords", icon: KeyRound, label: "Password Manager", roles: ROLE_ALL },
        { to: "/feedback", icon: MessageSquare, label: "Feedback", badge: unreadFeedback, roles: ROLE_ALL },
      ],
    },
  ];

  const currentRole = isAdmin ? "admin" : isSupervisor ? "supervisor" : "employee";
  // Filter each group's items by current role, then drop empty groups.
  const visibleGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => i.roles.includes(currentRole)) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen bg-background transition-colors duration-200" data-testid="app-layout">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
        <img
          src="/smj-logo-transparent.png"
          alt="Steele Martin Jones"
          className="h-10 ml-4"
        />
        {/* Theme toggle for mobile */}
        <div className="ml-auto flex items-center gap-2">
          <Sun size={16} className="text-muted-foreground" />
          <Switch
            checked={isDark}
            onCheckedChange={toggleTheme}
            data-testid="theme-toggle-mobile"
          />
          <Moon size={16} className="text-muted-foreground" />
        </div>
      </header>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full ${
          collapsed ? "lg:w-[72px]" : "lg:w-64"
        } w-64 bg-card border-r border-border z-50 transition-[width,transform] duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <TooltipProvider delayDuration={150}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`border-b border-border flex flex-col items-center ${collapsed ? "p-3" : "p-6"}`}>
            <img
              src="/smj-logo-transparent.png"
              alt="Steele Martin Jones CPAs"
              className={`${collapsed ? "h-10" : "h-16"} object-contain transition-all`}
            />
            {!collapsed && (
              <span className="text-xs tracking-[0.2em] font-medium text-primary mt-2">
                EMPLOYEE PORTAL
              </span>
            )}
          </div>

          {/* Collapse toggle (desktop only) */}
          <button
            type="button"
            onClick={toggleCollapsed}
            className="hidden lg:flex items-center justify-center self-end mt-2 mr-2 w-7 h-7 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            data-testid="sidebar-collapse-toggle"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>

          {/* Navigation */}
          <nav
            className={`flex-1 ${collapsed ? "px-2 py-3" : "p-4"} space-y-1 overflow-y-auto`}
            data-testid="sidebar-nav"
          >
            {visibleGroups.map((group, gIdx) => (
              <div
                key={group.label}
                className={gIdx > 0 ? (collapsed ? "mt-3" : "mt-5") : ""}
                data-testid={`sidebar-group-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {!collapsed && (
                  <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
                    {group.label}
                  </div>
                )}
                {collapsed && gIdx > 0 && (
                  <div className="my-2 mx-3 h-px bg-border/60" aria-hidden="true" />
                )}
                <div className="space-y-1">
                {group.items.map((link) => {
              const linkContent = (
                <>
                  <link.icon size={20} className="shrink-0" />
                  <span className={collapsed ? "lg:hidden" : ""}>{link.label}</span>
                  {link.badge > 0 && (
                    <Badge
                      className={`bg-blue-500 text-white text-xs ${
                        collapsed ? "lg:absolute lg:top-1 lg:right-1 lg:px-1 lg:py-0" : "ml-auto"
                      }`}
                    >
                      {link.badge}
                    </Badge>
                  )}
                  {!collapsed && !link.crossDomain && (
                    <ChevronRight
                      size={16}
                      className={`ml-auto opacity-0 group-hover:opacity-100 ${
                        link.badge > 0 ? "hidden" : ""
                      }`}
                    />
                  )}
                </>
              );

              const baseClass = `sidebar-link ${collapsed ? "lg:justify-center lg:px-0" : ""}`;
              const testId = `nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`;

              let linkInner;
              if (link.crossDomain) {
                // Cross-domain SSO link: inject current JWT into __TOKEN__ placeholder
                const handleCrossDomain = (e) => {
                  e.preventDefault();
                  const token = localStorage.getItem("token") || "";
                  const url = link.to.replace("__TOKEN__", encodeURIComponent(token));
                  window.open(url, "_blank", "noopener,noreferrer");
                  setSidebarOpen(false);
                };
                linkInner = (
                  <a
                    key={link.to}
                    href="#"
                    onClick={handleCrossDomain}
                    className={baseClass}
                    data-testid={testId}
                  >
                    {linkContent}
                  </a>
                );
              } else {
                linkInner = (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.end}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? "active" : ""} ${
                        collapsed ? "lg:justify-center lg:px-0" : ""
                      }`
                    }
                    data-testid={testId}
                  >
                    {linkContent}
                  </NavLink>
                );
              }

              // Wrap with tooltip ONLY when collapsed on desktop
              if (collapsed) {
                return (
                  <Tooltip key={link.to}>
                    <TooltipTrigger asChild>
                      <div className="relative">{linkInner}</div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="hidden lg:block">
                      {link.label}
                      {link.badge > 0 && ` (${link.badge})`}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return <div key={link.to}>{linkInner}</div>;
            })}
                </div>
              </div>
            ))}
          </nav>

          {/* User section - fixed at bottom */}
          <div className={`border-t border-border mt-auto bg-card ${collapsed ? "p-2" : "p-4"}`}>
            {/* Dark Mode Toggle */}
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className="w-full flex items-center justify-center py-2 rounded-lg hover:bg-muted/70 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="theme-toggle-rail"
                    aria-label="Toggle theme"
                  >
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="hidden lg:block">
                  {isDark ? "Light mode" : "Dark mode"}
                </TooltipContent>
              </Tooltip>
            ) : (
              <div className="flex items-center justify-between px-2 py-2 mb-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Sun size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Light</span>
                </div>
                <Switch
                  checked={isDark}
                  onCheckedChange={toggleTheme}
                  data-testid="theme-toggle"
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Dark</span>
                  <Moon size={16} className="text-muted-foreground" />
                </div>
              </div>
            )}

            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-full flex items-center justify-center my-2">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="hidden lg:block">
                  {user?.name} · <span className="capitalize">{user?.role}</span>
                </TooltipContent>
              </Tooltip>
            ) : (
              <div className="flex items-center gap-3 mb-3 px-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate text-sm">
                    {user?.name}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </div>
            )}

            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center py-2 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
                    data-testid="logout-button"
                    aria-label="Sign out"
                  >
                    <LogOut size={18} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="hidden lg:block">
                  Sign out
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="outline"
                className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut size={18} />
                Sign Out
              </Button>
            )}
          </div>
        </div>
        </TooltipProvider>
      </aside>

      {/* Main content */}
      <main
        className={`${
          collapsed ? "lg:pl-[72px]" : "lg:pl-64"
        } min-h-screen pt-16 lg:pt-0 transition-[padding] duration-200`}
      >
        <ImpersonationBanner />
        <div className="p-6 lg:p-8">
          {location.pathname !== "/" && (
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5 -ml-1 px-2 py-1 rounded-md hover:bg-muted/60"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
          )}
          <Outlet />
        </div>
      </main>
      <WhatsNewDialog />
      <InactivityWarningDialog
        open={showWarning}
        secondsLeft={secondsLeft}
        onStay={stayLoggedIn}
      />
    </div>
  );
};

export default Layout;
